// Socket.IO-клиент: JWT в handshake, авто-переподключение (websocket → long-polling fallback),
// обработка событий сервера → стор, звуки и haptics.
import i18n from '@/i18n';
import { haptic } from '@/lib/device';
import { fmt } from '@/lib/format';
import { notifyGameChanged } from '@/lib/hooks';
import { isPersonalBattleReport } from '@/lib/personalBattle';
import { play } from '@/lib/sound';
import { useGame } from '@/store/game';
import type { S2CEvents } from '@sk/shared';
import { type Socket, io } from 'socket.io-client';
import { API_BASE, getAccessToken, onTokenChange, post, refreshSession } from './client';
import type { MapCombatEvent } from './types';

let socket: Socket | null = null;

/** События сервера, после которых экраны перечитывают замок, армию и походы. */
const GAME_CHANGE_EVENTS = new Set([
  'alliance:history',
  'building:done',
  'army:trained',
  'march:update',
  'march:arrived',
  'attack:incoming',
  'battle:report',
  'notification',
  'hero:levelup',
  'ruling:changed',
]);
type Handler = (payload: unknown) => void;
const extra = new Map<string, Set<Handler>>();

export function getSocket(): Socket | null {
  return socket;
}

/** Подписка компонентов на события (возвращает отписку). */
type ClientEvents = S2CEvents & { 'map:combat': MapCombatEvent };
export function onSocket<E extends keyof ClientEvents>(
  event: E,
  fn: (p: ClientEvents[E]) => void,
): () => void {
  let set = extra.get(event);
  if (!set) {
    set = new Set();
    extra.set(event, set);
  }
  set.add(fn as Handler);
  return () => set?.delete(fn as Handler);
}

let ackTimer: number | null = null;
function ackDelivered(): void {
  if (ackTimer) return;
  ackTimer = window.setTimeout(() => {
    ackTimer = null;
    void post('/messages/delivered', {}).catch(() => undefined);
  }, 800);
}

function dispatch(event: string, payload: unknown): void {
  for (const fn of extra.get(event) ?? []) fn(payload);
}

/** Отправка события с подтверждением (ack) и таймаутом. */
export function emitAck<T = unknown>(event: string, payload: unknown, timeoutMs = 8000): Promise<T> {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('offline'));
      return;
    }
    socket
      .timeout(timeoutMs)
      .emit(
        event,
        payload,
        (err: Error | null, res: { ok: boolean; data?: T; error?: { message: string; code: string } }) => {
          if (err) reject(err);
          else if (!res.ok)
            reject(Object.assign(new Error(res.error?.message ?? 'error'), { code: res.error?.code }));
          else resolve(res.data as T);
        },
      );
  });
}

export function connectSocket(): void {
  const token = getAccessToken();
  if (!token) return;
  if (socket) {
    socket.auth = { token };
    if (!socket.connected) socket.connect();
    return;
  }
  const opts = {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15000,
  };
  // Яндекс Игры: игра на домене Яндекса — сокет к серверу игры по абсолютному адресу
  socket = API_BASE ? io(API_BASE, opts) : io(opts);
  const g = useGame.getState;
  socket.on('connect', () => {
    g().setConnected(true);
    // После разрыва связи часть событий могла потеряться — перечитываем состояние
    notifyGameChanged();
    window.dispatchEvent(new Event('sk:map-changed'));
  });
  socket.on('disconnect', () => g().setConnected(false));
  socket.on('connect_error', async (err) => {
    g().setConnected(false);
    if (/invalid_token|token_revoked|no_token/.test(err.message)) {
      if (await refreshSession()) {
        if (socket) socket.auth = { token: getAccessToken() };
      }
    }
  });
  socket.on('resources:update', (p: S2CEvents['resources:update']) => {
    const { gems, rates, at: _at, castleId, capacity: _cap, ...res } = p;
    // Сервер шлёт ресурсы каждого замка; берём только выбранный
    const current = g().castle?.castle.id;
    if (castleId && current && castleId !== current) return;
    g().setResources(res, gems, rates as never);
  });
  socket.on('map:update', (p: S2CEvents['map:update']) => {
    window.dispatchEvent(new Event('sk:map-changed'));
    // Захват или потеря Места силы меняет бонус производства у участников альянсов.
    if (p.tiles.some((tile) => tile.objectType === 'capital' || tile.objectType === 'oasis'))
      notifyGameChanged();
  });
  socket.on('chat:message', (m: S2CEvents['chat:message']) => {
    // Лента своего группового чата — отдельно для каждого чата
    g().addChat(m.channel === 'group' ? `group:${m.chatId ?? ''}` : m.channel, m);
  });
  socket.on('chat:deleted', (m: S2CEvents['chat:deleted']) => g().removeChat(m.id));
  socket.on('online:count', (p: S2CEvents['online:count']) => g().setOnline(p.total));
  socket.on('attack:incoming', (p: S2CEvents['attack:incoming']) => {
    haptic([80, 60, 80]);
    play('hit');
    g().toast(i18n.t('game:notifications.attack_incoming', { from: p.fromUser }), 'warning');
  });
  socket.on('battle:report', (p: S2CEvents['battle:report']) => {
    if (!isPersonalBattleReport(g().user?.id, p)) return;
    play('report');
    haptic(40);
    const won = (p.report.winner === 'attacker') === (p.attackerId === g().user?.id);
    g().toast(won ? i18n.t('game:battle.victory') : i18n.t('game:battle.defeat'), won ? 'success' : 'error');
  });
  socket.on('building:done', () => {
    play('fanfare');
    g().toast(i18n.t('game:notifications.building_done'), 'success');
  });
  // Смена правящего — спокойное уведомление (без салюта на весь экран)
  socket.on('ruling:changed', (p: S2CEvents['ruling:changed']) => {
    play('crown');
    g().toast(`👑 ${i18n.t('politics:changed', { name: p.newAllianceName || '—' })}`, 'info');
  });
  socket.on('tax:charged', (p: S2CEvents['tax:charged']) => {
    if (p.resourceType === 'silver')
      g().toast(
        i18n.t('politics:taxCharged', {
          n: fmt(p.amount),
          res: i18n.t(`common:resources.${p.resourceType}`),
          rate: p.rate,
        }),
      );
  });
  socket.on('system:broadcast', (p: S2CEvents['system:broadcast']) =>
    g().toast(`📢 ${p.text}`, p.level === 'warning' ? 'warning' : 'info'),
  );
  socket.on('session:revoked', () => {
    socket?.disconnect();
    location.assign('/login');
  });
  socket.on('notification', (p: S2CEvents['notification']) => {
    // Новое письмо дошло до открытой игры — у отправителя две серые галочки
    if (p.type === 'mail') ackDelivered();
    // Открытую переписку всплывашка только перекрыла бы: письмо и так появляется в ней
    if (p.type === 'mail' && location.pathname.startsWith('/game/mail')) return;
    if (p.type === 'mail' || p.type === 'news') play(p.type);
    const key = `game:notifications.${p.type}`;
    if (i18n.exists(key)) g().toast(i18n.t(key, p.payload as Record<string, string>));
  });
  socket.on('hero:levelup', () => {
    play('fanfare');
    g().toast(i18n.t('game:hero.levelUp'), 'success');
  });
  socket.onAny((event: string, payload: unknown) => {
    dispatch(event, payload);
    if (GAME_CHANGE_EVENTS.has(event)) notifyGameChanged();
  });

  // Экономия батареи: пинг раз в 25 с только при видимой вкладке; при скрытии — отключаемся через 5 мин
  let hiddenTimer: number | null = null;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenTimer = window.setTimeout(() => socket?.disconnect(), 5 * 60_000);
    } else {
      if (hiddenTimer) window.clearTimeout(hiddenTimer);
      if (socket && !socket.connected && getAccessToken()) {
        socket.auth = { token: getAccessToken() };
        socket.connect();
      }
    }
  });
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

onTokenChange((t) => {
  if (!t) disconnectSocket();
  else if (socket) socket.auth = { token: t };
});
